import { ObjectFormat } from '../uiObjects/objectFormat';

import type { Direction } from './direction';
import type { Line } from './Line';
import { MathObject } from './MathObject';

export const SLASH_HORIZONTAL_PADDING = 3;

const SLASH_HORIZONTAL_OVERSHOOT = 5;
const SLASH_LOWER_LEFT_VERTICAL_OFFSET = 4;
const SLASH_UPPER_RIGHT_VERTICAL_OFFSET = 2;
const MIN_SLASH_VERTICAL_RISE = 2;

export class SlashedObject extends MathObject {
  child: MathObject;

  constructor(child: MathObject, format?: ObjectFormat) {
    super();

    this.t = 'slashedObject';
    this.gmmName = 'slashedObject';
    this.child = child;
    this.children.push(child);
    this.child.parent = this;
    this.child.locationParent = this;

    this.setObjectFormat(
      ObjectFormat.merge(
        new ObjectFormat(
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          true
        ),
        format
      )
    );
    this.buildSize();
  }

  toXML(): string {
    return this.child.toXML();
  }

  isSlashedObject(): boolean {
    return true;
  }

  buildSizeRecursive(): void {
    this.child.buildSizeRecursive();
    this.buildSize();
  }

  buildSize(): void {
    this.child.viewportX = SLASH_HORIZONTAL_PADDING;
    this.child.viewportY = 0;
    this.setAllDim(
      this.child.viewportW + SLASH_HORIZONTAL_PADDING * 2,
      this.child.viewportH
    );
  }

  setLine(line: Line): void {
    super.setLine(line);
    this.child.setLine(line);
  }

  setEditable(editable: boolean): void {
    this.child.setEditable(editable);
  }

  setFont(font: string): void {
    this.child.setFont(font);
  }

  grabFocus(dir?: Direction): void {
    this.child.grabFocus(dir);
  }

  swapString(old: string, replacer: string): void {
    this.child.swapString(old, replacer);
  }

  childSizeChanged(): void {
    this.buildSize();
    this.getValueParent()?.childSizeChanged();
  }

  isBaselineAware(): boolean {
    return this.child.isBaselineAware();
  }

  getVertToBaseline(): number {
    return this.child.getVertToBaseline();
  }

  countMiddleSymbols(): number {
    return this.child.countMiddleSymbols();
  }

  pixelsToMiddleSymbol(whichMiddleSymbol?: number): number {
    const childPixelsToMiddle = this.child.pixelsToMiddleSymbol(
      whichMiddleSymbol
    );

    return childPixelsToMiddle === 0
      ? 0
      : SLASH_HORIZONTAL_PADDING + childPixelsToMiddle;
  }

  getPaintedCharCount(): number {
    return this.child.getPaintedCharCount();
  }

  paintPost(ctx: CanvasRenderingContext2D): void {
    super.paintPost(ctx);
    this.paintSlash(ctx);
  }

  private paintSlash(ctx: CanvasRenderingContext2D): void {
    const slashWidth = this.viewportW - this.viewportMargin * 2;
    const slashHeight = this.viewportH - this.viewportMargin * 2;

    if (slashWidth <= 0 || slashHeight <= 0) {
      return;
    }

    const minDimension = Math.min(slashWidth, slashHeight);
    const inset = Math.min(2, minDimension / 4);

    // Preserve a visible diagonal when fixed endpoint offsets would flatten small or scaled values.
    const unoffsetVerticalRise = slashHeight - inset * 2;
    const minVerticalRise = Math.min(
      MIN_SLASH_VERTICAL_RISE,
      unoffsetVerticalRise
    );
    const upperRightVerticalOffset = Math.min(
      SLASH_UPPER_RIGHT_VERTICAL_OFFSET,
      Math.max(0, unoffsetVerticalRise - minVerticalRise)
    );
    const lowerLeftVerticalOffset = Math.min(
      SLASH_LOWER_LEFT_VERTICAL_OFFSET,
      Math.max(
        0,
        unoffsetVerticalRise - upperRightVerticalOffset - minVerticalRise
      )
    );
    const lineFillStyle = (this.child as { line?: { fillStyle?: string } }).line
      ?.fillStyle;

    ctx.save();
    ctx.beginPath();
    ctx.strokeStyle =
      this.objectFormat?.color ||
      this.child.fillStyle ||
      lineFillStyle ||
      'black';
    ctx.lineCap = 'round';
    ctx.lineWidth = Math.max(2, Math.min(4, Math.ceil(minDimension / 12)));
    ctx.moveTo(
      SLASH_HORIZONTAL_PADDING + inset - SLASH_HORIZONTAL_OVERSHOOT,
      slashHeight - inset - lowerLeftVerticalOffset
    );
    ctx.lineTo(
      slashWidth -
        SLASH_HORIZONTAL_PADDING -
        inset +
        SLASH_HORIZONTAL_OVERSHOOT,
      inset + upperRightVerticalOffset
    );
    ctx.stroke();
    ctx.restore();
  }
}
